import type { RegistrationQueryResponse } from '~/models/MLI/generated'

export interface ZorgIdSession {
  sessionId: string
  nonce?: string
  failureReason: string
}

const zorgIdSession = ref<ZorgIdSession | null>()
const isSessionActive = ref<boolean>(false)
const isLoginInProgress = ref<boolean>(false)
const isRegistrationInProgress = ref<boolean>(false)
const isDeregistrationInProgress = ref<boolean>(false)
const isGettingExpirationDateInProgress = ref<boolean>(false)
const tokenExpirationDate = ref<string | null>(null)

export default function useZorgId() {
  const { t } = useI18n()

  const { lspApiGet, lspApiPost } = useApi()
  const { toast } = useHelpers()
  const { reportError } = useSentry()
  const config = useRuntimeConfig()

  const hasValidLspToken = computed(() => {
    if (!tokenExpirationDate.value) return false

    const today = new Date()
    const expirationDate = new Date(tokenExpirationDate.value)
    return expirationDate >= today
  })

  async function uziLogin(): Promise<void> {
    try {
      await _createSession()
      await _openSession()
    }
    catch (error) {
      _reset()
      _handleError(error, t('zorgId.loginFailed'))
      return
    }
  }

  async function createRegistrationToken(bsn: string): Promise<void> {
    if (!isSessionActive.value) {
      toast.error(t('zorgId.loginRequired'))
      return
    }

    try {
      await _createRegistrationToken(bsn)
    }
    catch (error) {
      _reset()
      _handleError(error, t('zorgId.registrationFailed'))
    }
  }

  async function deleteRegistrationToken(bsn: string): Promise<void> {
    try {
      await _deleteRegistrationToken(bsn)
    }
    catch (error) {
      _reset()
      _handleError(error, t('zorgId.deregistrationFailed'))
    }
  }

  async function getTokenExpirationDate(bsn: string): Promise<string | null> {
    if (!bsn) {
      throw new Error('BSN is required to get expiration date')
    }

    isGettingExpirationDateInProgress.value = true

    try {
      const tokenExpirationResponse
        = await lspApiPost<RegistrationQueryResponse>(
          'zorg-id/registration/query',
          { bsn },
        )

      isGettingExpirationDateInProgress.value = false

      return tokenExpirationResponse.validUntil
    }
    catch {
      isGettingExpirationDateInProgress.value = false
      return null
    }
  }

  async function _createRegistrationToken(bsn: string) {
    isRegistrationInProgress.value = true
    const result = await lspApiPost<{
      sessionId: string
      transactionId: string
      failureReason: string
    }>('zorg-id/registration', {
      sessionId: zorgIdSession.value!.sessionId,
      bsn,
    })

    _checkFailureReason(result)
    isRegistrationInProgress.value = false

    updateTokenExpirationDate(bsn)
    toast.success(t('zorgId.registrationSuccess'))
  }

  async function _deleteRegistrationToken(bsn: string) {
    isDeregistrationInProgress.value = true
    await lspApiPost('zorg-id/registration/delete', {
      bsn,
    })

    isDeregistrationInProgress.value = false

    updateTokenExpirationDate(bsn)
    toast.success(t('zorgId.registrationSuccess'))
  }

  async function _createSession() {
    isLoginInProgress.value = true

    const result = await lspApiPost<ZorgIdSession>('zorg-id/session')
    zorgIdSession.value = result

    _checkFailureReason(zorgIdSession.value)
  }

  async function _openSession() {
    const promise = lspApiGet<ZorgIdSession>(
      'zorg-id/session/{sessionId}',
      {},
      { sessionId: zorgIdSession.value!.sessionId },
    )

    window.open(
      `zorgid://s?n=${zorgIdSession.value!.nonce}&e=${config.public.ZORGID_ENV}`,
    )

    const result = await promise
    zorgIdSession.value = result

    _checkFailureReason(zorgIdSession.value)

    isSessionActive.value = true
    isLoginInProgress.value = false
  }

  function _checkFailureReason(session: ZorgIdSession): void {
    if (session.failureReason !== 'None') {
      throw new Error(session.failureReason)
    }
  }

  function _handleError(error: unknown, toastMessage: string) {
    console.error('ZorgID error:', error)
    toast.error(toastMessage)

    const errorMessage = error instanceof Error ? error.message : String(error)

    reportError(errorMessage, { extra: { session: zorgIdSession.value } })
  }

  function _reset() {
    zorgIdSession.value = null
    isSessionActive.value = false
    isLoginInProgress.value = false
    isRegistrationInProgress.value = false
  }

  async function updateTokenExpirationDate(bsn: string) {
    tokenExpirationDate.value = await getTokenExpirationDate(bsn)
  }

  return {
    uziLogin,
    createRegistrationToken,
    deleteRegistrationToken,
    getTokenExpirationDate,
    updateTokenExpirationDate,
    isSessionActive,
    zorgIdSession,
    inProgress: isLoginInProgress,
    isRegistering: isRegistrationInProgress,
    isGettingExpirationDate: isGettingExpirationDateInProgress,
    hasValidLspToken,
    tokenExpirationDate,
  }
}
